import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Close from '@icon/Close';
import Input from '@base/TextField/TextField';
import { InputAdornment } from '@material-ui/core';
import { useRouter } from 'next/router';
import useWindowSize from '@hooks/useWindowSize';
import NoActivityYet from 'aquilla/icons/emptyStates/NoActivityYet';
import LeftChevron from 'aquilla/icons/actions/LeftChevron';
import Dynamic from '@base/Dynamic/Dynamic';
import { getAnimationPath } from '@utils/animationHelpers';
import { segment } from '@actions/analytics';
import useMounted from '@hooks/useMounted';
import { CHECKOUT_REFERRAL_CODE_STARTED } from './constants/segment';
import useReferralCodeSuggestions from './useReferralCodeSuggestions';
import SuggestedCode from './SuggestedCode';

const LottieAnimation = Dynamic(() =>
  import('@base/LottieAnimation/LottieAnimation')
);

const Container = styled.div`
  width: 100%;
  grid-column: 1/12;
  background: var(--color-base-light-0);

  ${mediaQuery.aboveTablet} {
    height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
  }
`;

const Header = styled.div`
  padding: ${Spacings.SPACING_8B} 116px ${Spacings.SPACING_4B}
    ${Spacings.SPACING_12B};
  z-index: 1;
  transition: all 0.3s ease-in-out;
  position: sticky;

  ${mediaQuery.aboveTablet} {
    width: 620px;
    top: ${({ $scrolled }) => ($scrolled ? `-${Spacings.SPACING_16B}` : '0px')};
    transform: ${({ $scrolled }) =>
      $scrolled ? `translate3d(${Spacings.SPACING_9B}, 0, 0)` : ''};

    ::before {
      content: ' ';
      display: block;
      background-color: var(--color-base-1);
      position: absolute;
      height: calc(100% + ${Spacings.SPACING_15B});
      top: -${Spacings.SPACING_16B};
      transform: translateX(-${Spacings.SPACING_21B});
      width: calc(100% + ${Spacings.SPACING_21B});
      z-index: -1;
      border-bottom: ${({ $scrolled, $isEmpty }) =>
        $scrolled && !$isEmpty ? '1px solid var(--color-i-divider)' : '0'};
    }
  }

  ${mediaQuery.uptoTablet} {
    top: 0;
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_4B}
      ${Spacings.SPACING_3B};
    background: var(--color-base-1);
    border-bottom: ${({ $scrolled, $isEmpty }) =>
      $scrolled && !$isEmpty ? '1px solid var(--color-i-divider)' : '0'};

    h3 {
      font-size: ${Fonts.H5};
      line-height: 24px;
    }
  }
`;

const Heading = styled.div`
  display: flex;

  ${mediaQuery.aboveTablet} {
    flex-direction: column;
  }
`;

const CodesSection = styled.div`
  padding: ${Spacings.SPACING_4B} 116px ${Spacings.SPACING_4B}
    ${Spacings.SPACING_12B};
  background: var(--color-base-light-0);

  ${mediaQuery.uptoTablet} {
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_4B} 0;
  }
`;

const CloseWrapper = styled.div`
  grid-column: 1/11;
  cursor: pointer;
  width: min-content;

  ${mediaQuery.aboveTablet} {
    margin-bottom: ${Spacings.SPACING_8B};
    position: sticky;
    top: ${Spacings.SPACING_8B};
    padding-left: ${Spacings.SPACING_12B};
    z-index: 10;
  }

  ${mediaQuery.uptoTablet} {
    margin-right: ${Spacings.SPACING_4B};
  }
`;

const InputGroup = styled.div`
  display: flex;
  margin: 18px 0 ${Spacings.SPACING_2B};
`;

const StyledInput = styled(Input)`
  flex: 1;
  height: 48px;

  &.MuiOutlinedInput-root .MuiOutlinedInput-input {
    padding: ${Spacings.SPACING_4B};
  }

  &.MuiInputBase-root {
    border-color: ${({ $isError }) =>
      $isError ? 'var(--color-i-red)' : 'var(--color-divider)'} !important;
    padding-right: ${Spacings.SPACING_4B};
  }
`;

const ApplyBtn = styled(Button)`
  width: 103px;
  height: ${Spacings.SPACING_12B};
  min-height: ${Spacings.SPACING_12B};
  margin-left: ${Spacings.SPACING_4B};
  border-color: var(--color-text-primary);

  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.H5};
  }
`;

const ClearButton = styled(Close)`
  cursor: pointer;
`;

const NoCodes = styled.div`
  text-align: center;
  max-width: 300px;
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 210px);

  ${mediaQuery.uptoTablet} {
    height: calc(100vh - 130px);
  }
`;

const NoCodesHeading = styled.div`
  font-size: ${Fonts.H5};
  line-height: 24px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-top: ${Spacings.SPACING_4B};
`;

const CodeValue = styled.div`
  font-size: ${Fonts.H6};
  line-height: 20px;
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_1B};
`;

const AnimationContainer = styled(LottieAnimation)`
  height: ${Spacings.SPACING_20B};
`;

const ReferralSuggestions = ({
  onClose,
  onApply,
  referralCode = '',
  validateReferralInProgress,
  isReferralValid,
  referralMessage,
  isSuggestionFlow = true,
  mwebCheckoutV2 = false,
  subscriptionUid = null
}) => {
  const [code, setCode] = useState(referralCode);
  const [justClicked, setJustClicked] = useState(false);

  useEffect(() => {
    setCode(referralCode);
  }, [referralCode]);

  useEffect(() => {
    if (!validateReferralInProgress && isReferralValid) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateReferralInProgress]);

  const endAdornment = () => {
    return (
      <InputAdornment position="end">
        <ClearButton
          size="16px"
          onClick={() => {
            onApply('');
            setCode('');
          }}
        />
      </InputAdornment>
    );
  };

  const containerRef = useRef();

  const [scrolled, setIsScrolled] = useState(false);

  const isMounted = useMounted();

  const onScroll = useCallback(() => {
    if (containerRef.current.scrollTop === 0) {
      setIsScrolled(false);
    } else {
      setIsScrolled(true);
    }
  }, []);

  useEffect(() => {
    const containerCurrentRef = containerRef.current;
    if (containerCurrentRef && isMounted) {
      containerCurrentRef.addEventListener('scroll', onScroll);
      containerCurrentRef.addEventListener('touchmove', onScroll);
    }
    return () => {
      if (containerCurrentRef) {
        containerCurrentRef.removeEventListener('scroll', onScroll);
        containerCurrentRef.removeEventListener('touchmove', onScroll);
      }
    };
  }, [isMounted, onScroll]);

  const { isMobile } = useWindowSize();

  const router = useRouter();
  const { subscriptionUID = subscriptionUid } = router.query ?? subscriptionUid;

  const { referralCodes, isLoading } =
    useReferralCodeSuggestions(subscriptionUID);

  const activeCodes = useMemo(() => {
    return referralCodes.filter(({ isActive }) => !!isActive);
  }, [referralCodes]);

  const inactiveCodes = useMemo(() => {
    return referralCodes.filter(({ isActive }) => !isActive);
  }, [referralCodes]);

  useEffect(() => {
    if (referralCodes) {
      segment.track(CHECKOUT_REFERRAL_CODE_STARTED, {
        codes_available: referralCodes.length,
        maximum_discount:
          activeCodes?.length > 0 ? activeCodes[0].discountPercent : 0
      });
    }
  }, [referralCodes, activeCodes]);

  const isError =
    !validateReferralInProgress && referralCode && referralMessage;

  const handleOnApply = () => {
    onApply(code);
    setTimeout(() => {
      setJustClicked(true);
    }, 100);
  };

  return (
    <Container ref={containerRef}>
      {!isMobile && (
        <CloseWrapper>
          <Close
            size="24px"
            onClick={onClose}
            color="var(--color-text-primary)"
          />
        </CloseWrapper>
      )}
      <Header
        $scrolled={scrolled}
        $isEmpty={isLoading || referralCodes?.length === 0}
      >
        <Heading>
          {isMobile && (
            <CloseWrapper onClick={onClose}>
              <LeftChevron size="24px" color="var(--color-text-primary)" />
            </CloseWrapper>
          )}
          {!mwebCheckoutV2 && (
            <Typography variant="h3">Coupons and offers</Typography>
          )}
          {mwebCheckoutV2 && (
            <Typography variant="h3">Apply discount code</Typography>
          )}
        </Heading>
        <InputGroup>
          <StyledInput
            $isError={isError}
            placeholder="Enter code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            endAdornment={code ? endAdornment() : null}
          />
          <ApplyBtn
            label="Apply"
            variant="secondary"
            color="black"
            onClick={handleOnApply}
            isLoading={referralCode === code && validateReferralInProgress}
          />
        </InputGroup>
        {justClicked && isError && (
          <Typography variant="p3" color="var(--color-i-red)">
            {referralMessage}
          </Typography>
        )}
      </Header>
      {isSuggestionFlow && referralCodes.length > 0 && (
        <CodesSection $isError={isError}>
          {activeCodes.length > 0 && (
            <div>
              <Typography variant="p3" color="var(--color-text-secondary)">
                Active offers
              </Typography>
              {activeCodes.map((codeDetails, i) => {
                return (
                  <SuggestedCode
                    key={codeDetails.code}
                    code={codeDetails}
                    onApply={onApply}
                    isActive
                    isFirst={i === 0}
                    referralCode={referralCode}
                    validateReferralInProgress={validateReferralInProgress}
                  />
                );
              })}
            </div>
          )}
          {inactiveCodes.length > 0 && (
            <div>
              <Typography variant="p3" color="var(--color-text-secondary)">
                Other offers
              </Typography>
              {inactiveCodes.map((codeDetails) => {
                return (
                  <SuggestedCode
                    key={codeDetails.code}
                    code={codeDetails}
                    referralCode={referralCode}
                    onApply={onApply}
                    validateReferralInProgress={validateReferralInProgress}
                  />
                );
              })}
            </div>
          )}
        </CodesSection>
      )}
      {!isLoading && referralCodes.length === 0 && (
        <NoCodes>
          <NoActivityYet height={80} width={80} />
          <NoCodesHeading>No saved coupons</NoCodesHeading>
          <CodeValue>
            Give DST to get instant discount or ask your friends and teachers to
            refer you
          </CodeValue>
        </NoCodes>
      )}
      {isLoading && (
        <NoCodes>
          <AnimationContainer
            // @ts-ignore
            src={getAnimationPath('searching-codes.json')}
          />
          <CodeValue>Looking for all coupons and offers</CodeValue>
        </NoCodes>
      )}
    </Container>
  );
};

export default ReferralSuggestions;
